import type { ReactNode } from "react";
import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { TABLE, TD, TH, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { NashEquilibriumStrings } from "@/components/tools/nash-equilibrium-calculator";
import { t } from "@/lib/i18n/interpolate";
import { minus } from "@/lib/tool-kit/econ-micro/format";
import {
  colBestResponses,
  dominantStrategy,
  type Game,
  iteratedElimination,
  mixedEquilibrium2x2,
  paretoImprovements,
  pureEquilibria,
  rowBestResponses,
  size,
} from "@/lib/tool-kit/econ-micro/game";

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

interface Props {
  game: Game;
  rowName: string;
  colName: string;
  rowStrategies: string[];
  colStrategies: string[];
  strings: NashEquilibriumStrings;
  fmt: Fmt;
}

/** The payoff matrix with each player's best responses underlined and equilibria boxed. */
function BestResponseTable({
  game,
  rowName,
  colName,
  rowStrategies,
  colStrategies,
  strings: s,
  fmt,
}: Props) {
  const n = (v: number) => minus(fmt(v, { maximumFractionDigits: 4 }));
  const rowBest = rowBestResponses(game);
  const colBest = colBestResponses(game);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{s.tableHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
            {t(s.tableCaption, { row: rowName, col: colName })}
          </caption>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {rowName} ↓ / {colName} →
              </th>
              {colStrategies.map((name, j) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: strategies are positional
                <th className={TH} key={j} scope="col">
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowStrategies.map((name, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: strategies are positional
              <tr key={i}>
                <th className={`${TD} font-medium`} scope="row">
                  {name}
                </th>
                {colStrategies.map((_, j) => {
                  const equilibrium = rowBest[i][j] && colBest[i][j];
                  return (
                    <td
                      className={`${TD} font-mono ${equilibrium ? "bg-primary/10 font-semibold" : ""}`}
                      // biome-ignore lint/suspicious/noArrayIndexKey: strategies are positional
                      key={j}
                    >
                      <span
                        className={
                          rowBest[i][j] ? "underline decoration-2" : ""
                        }
                      >
                        {n(game.row[i][j])}
                      </span>
                      {", "}
                      <span
                        className={
                          colBest[i][j] ? "underline decoration-2" : ""
                        }
                      >
                        {n(game.col[i][j])}
                      </span>
                      {equilibrium ? (
                        <span className="sr-only"> {s.equilibriumMark}</span>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-muted-foreground text-xs">{s.tableNote}</p>
    </div>
  );
}

function MixedSection({
  game,
  rowName,
  colName,
  rowStrategies,
  colStrategies,
  strings: s,
  fmt,
}: Props) {
  const n = (v: number) => minus(fmt(v, { maximumFractionDigits: 4 }));
  const { rows, cols } = size(game);
  const mixed = mixedEquilibrium2x2(game);
  let body: ReactNode = (
    <p className="text-muted-foreground text-sm">{s.mixedLarger}</p>
  );
  if (rows === 2 && cols === 2) {
    body = <p className="text-foreground text-sm">{s.mixedNone}</p>;
  }
  if (mixed) {
    body = (
      <>
        <p className="text-foreground text-sm">
          {t(s.mixedResult, {
            row: rowName,
            col: colName,
            rowStrategy: rowStrategies[0],
            colStrategy: colStrategies[0],
            rowPercent: fmt(mixed.rowProbability, {
              style: "percent",
              maximumFractionDigits: 2,
            }),
            colPercent: fmt(mixed.colProbability, {
              style: "percent",
              maximumFractionDigits: 2,
            }),
          })}
        </p>
        <p className="text-foreground text-sm">
          {t(s.mixedPayoffs, {
            rowPayoff: n(mixed.rowPayoff),
            colPayoff: n(mixed.colPayoff),
          })}
        </p>
        <p className="text-muted-foreground text-sm">{s.mixedWhy}</p>
      </>
    );
  }
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{s.mixedHeading}</h3>
      {body}
    </div>
  );
}

export function NashView(props: Props) {
  const {
    game,
    rowName,
    colName,
    rowStrategies,
    colStrategies,
    strings: s,
    fmt,
  } = props;
  const n = (v: number) => minus(fmt(v, { maximumFractionDigits: 4 }));
  const equilibria = pureEquilibria(game);
  const rowDominant = dominantStrategy(game, "row");
  const colDominant = dominantStrategy(game, "col");
  const reduction = iteratedElimination(game);
  const dominantText = (
    who: string,
    found: ReturnType<typeof dominantStrategy>,
    names: string[]
  ) =>
    found
      ? t(found.kind === "strict" ? s.dominantStrict : s.dominantWeak, {
          player: who,
          strategy: names[found.index],
        })
      : t(s.dominantNone, { player: who });
  const survivors = reduction.rows.length * reduction.cols.length;
  return (
    <div className="space-y-5">
      <BestResponseTable {...props} />
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{s.pureHeading}</h3>
        {equilibria.length === 0 ? (
          <p className="text-foreground text-sm">{s.pureNone}</p>
        ) : (
          <StatGrid>
            {equilibria.map(([i, j]) => (
              <StatCard
                emphasis
                key={`${i}-${j}`}
                label={t(s.payoffsLabel, {
                  row: n(game.row[i][j]),
                  col: n(game.col[i][j]),
                })}
                value={t(s.outcome, {
                  rowStrategy: rowStrategies[i],
                  colStrategy: colStrategies[j],
                })}
              />
            ))}
          </StatGrid>
        )}
        {equilibria.map(([i, j]) => {
          const better = paretoImprovements(game, i, j);
          return better.length > 0 ? (
            <p className="text-muted-foreground text-sm" key={`p-${i}-${j}`}>
              {t(s.paretoNote, {
                rowStrategy: rowStrategies[i],
                colStrategy: colStrategies[j],
                better: better
                  .map(([r, c]) =>
                    t(s.outcome, {
                      rowStrategy: rowStrategies[r],
                      colStrategy: colStrategies[c],
                    })
                  )
                  .join(s.listJoin),
              })}
            </p>
          ) : null;
        })}
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{s.dominanceHeading}</h3>
        <p className="text-foreground text-sm">
          {dominantText(rowName, rowDominant, rowStrategies)}
        </p>
        <p className="text-foreground text-sm">
          {dominantText(colName, colDominant, colStrategies)}
        </p>
        {reduction.removed.length > 0 ? (
          <p className="text-foreground text-sm">
            {survivors === 1
              ? t(s.eliminationSolved, {
                  rowStrategy: rowStrategies[reduction.rows[0]],
                  colStrategy: colStrategies[reduction.cols[0]],
                })
              : t(s.eliminationPartial, {
                  rows: reduction.rows
                    .map((i) => rowStrategies[i])
                    .join(s.listJoin),
                  cols: reduction.cols
                    .map((j) => colStrategies[j])
                    .join(s.listJoin),
                })}
          </p>
        ) : (
          <p className="text-muted-foreground text-sm">{s.eliminationNone}</p>
        )}
      </div>
      <MixedSection {...props} />
    </div>
  );
}
